import { CLAIM_CHALLENGE, PROGRESS_TASK, START_CHALLENGE } from "shared/api";
import { AsyncButton, Icon, When } from "shared/react";
import { type AttemptData, type ChallengeData, isChallengeDone, isChallengeEarly, isChallengeEnded, isChallengeStartable } from "shared/schema";
import { callAPI } from "../util/api.js";
import { Task } from "./Tasks.js";

/** List of interactive challenge tasks that can be progressed along with `start` and `claim` buttons. */
export function ChallengeTasks({ id, challenge, attempt }: { id: string; challenge: ChallengeData; attempt: AttemptData | undefined }): React.ReactElement {
	const { tasks } = challenge;
	const totals = attempt?.totals || {};
	return (
		<>
			{Object.entries(tasks).map(([tid, task]) => (
				<Task //
					key={tid}
					id={tid}
					task={task}
					total={totals[tid]}
					onProgress={id && attempt ? ({ amount, units }) => callAPI(PROGRESS_TASK, { cid: id, tid, amount, units }) : undefined}
				/>
			))}
		</>
	);
}

export function ChallengeButtons({ id, challenge, attempt, mods }: { id: string; challenge: ChallengeData; attempt: AttemptData | undefined; mods?: string | undefined }): React.ReactElement | null {
	if (id) {
		if (attempt) {
			// There's an attempt, possibly show the `Claim challenge` button.
			const { tasks } = challenge;
			const { totals, completed } = attempt;
			if (completed === null && isChallengeDone(tasks, totals)) {
				return (
					<AsyncButton mods={`button-success ${mods}`} onClick={() => callAPI(CLAIM_CHALLENGE, id)}>
						<Icon icon="tick" /> <span>Claim challenge</span>
					</AsyncButton>
				);
			}
		} else {
			// There no attempt, possibly show the `Start challenge` button.
			if (!isChallengeEnded(challenge))
				return (
					<AsyncButton mods={mods} onClick={isChallengeStartable(challenge) ? () => callAPI(START_CHALLENGE, id) : undefined}>
						<Icon icon="add" /> <span>Start challenge</span>
					</AsyncButton>
				);
		}
	}
	return null;
}

export function ChallengeStatus({ challenge, attempt, mods = "" }: { challenge: ChallengeData; attempt: AttemptData | undefined; mods?: string | undefined }): React.ReactElement {
	if (!attempt) {
		return (
			<p className={`status ${mods}`}>
				{isChallengeEnded(challenge) ? (
					<span>
						Challenge closed <When target={challenge.end} />
					</span>
				) : isChallengeEarly(challenge) ? (
					<span>
						Challenge opens <When target={challenge.start} />
					</span>
				) : (
					"Challenge not started"
				)}
			</p>
		);
	} else {
		const { started, updated, completed } = attempt;
		return (
			<p className={`status ${mods}`}>
				{completed !== null ? (
					<span>
						Completed <When target={completed} />
					</span>
				) : updated !== null ? (
					<span>
						Progressed <When target={updated} />
					</span>
				) : (
					<span>
						Started <When target={started} />
					</span>
				)}
			</p>
		);
	}
}
